import { createRuntime, type Platform, type Runtime } from "@baton/core";
import type { I18n } from "@baton/i18n";
import { toast } from "@baton/ui";

export interface AppRuntimeOptions {
  readonly platform: Platform;
  /** The entry's catalogue, which the strings the runtime raises outside React are read through. */
  readonly i18n: I18n;
  readonly reload: () => void;
  readonly baseUrl?: string;
}

/**
 * The runtime of the authenticated entry (`main.tsx`): the core runtime with the words it raises
 * outside React read from the entry's catalogue. A queued write dropped on reconnect is a warning
 * toast of its own, and an action the instance refused `overloaded` after the transport waited
 * `Retry-After` between its attempts fails with "Baton is busy, try again in a moment." in place of
 * the server's words, so the toast that action raises says what the person can do rather than
 * which connection the instance lacked (`contracts/client-api.md` § Problems to surface, 2026-09-29).
 * A request that ran out of its own time fails with "The server took too long to answer" from the same
 * catalogue, apart from a network failure's "The request did not reach the server"
 * (`contracts/client-api.md` § Timeouts, 2026-10-13).
 */
export function createAppRuntime({ platform, i18n, reload, baseUrl }: AppRuntimeOptions): Runtime {
  return createRuntime({
    platform,
    reload,
    ...(baseUrl === undefined ? {} : { baseUrl }),
    overloadedWords: () => i18n.t("shell.busy"),
    timeoutWords: () => i18n.t("shell.timedOut"),
    onDroppedWrite: (dropped) => {
      toast({
        variant: "warning",
        title: i18n.t("shell.queue.dropped.title"),
        description: i18n.t("shell.queue.dropped.body", { item: dropped.itemLabel, fields: i18n.format.list(dropped.fields) }),
      });
    },
  });
}
